import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { BUILDING_ENTITY } from '../../../../constants/entity'
import {
  useAsyncCallback,
  useInitData,
  useLinkLikeClick,
  usePageBrowserTabTitle,
  usePageShowReload,
  useSelection,
} from '../../../../hooks'
import { getBuildingProfilePathname } from '../../../../utils/link'
import {
  useAsyncTable,
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../Table'

export function useBuildingTablePage({
  asyncTableOptions,
  entity = BUILDING_ENTITY,
  extraSelectionResetDeps = EMPTY_LIST,
  getPermanentFilters,
  pageTitle,
  sub = null,
  withBrowserTabTitle = true,
  onApplyTableConfig,
}) {
  const [isConfigOpen, setConfigOpen] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const asyncTable = useAsyncTable({
    ...asyncTableOptions,
    containerRef: pageScrollWrapperRef,
    entity,
    getPermanentFilters,
    sub,
  })

  const {
    columns,
    filterValues,
    handleReload,
    isInitiated,
    isRefetching,
    items,
    view,
  } = asyncTable

  usePageBrowserTabTitle({
    isEnabled: withBrowserTabTitle,
    isLoading: !isInitiated,
    pageTitle,
  })

  const selection = useSelection({
    data: items,
    pagination: asyncTable.pagination,
  })

  const { handleCleanSelection } = selection

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters,
    sub,
    onReload: handleReload,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  usePageShowReload(handleReload)

  useEffect(() => {
    handleCleanSelection()
  }, [...extraSelectionResetDeps, filterValues])

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const { availableSections } = useInitData()

  function handleClickRow(id, event) {
    handleClick({
      event,
      link: getBuildingProfilePathname({ availableSections, id }),
    })
  }

  function handleMouseDownRow(id, event) {
    handleMouseDown({
      event,
      link: getBuildingProfilePathname({ availableSections, id }),
    })
  }

  const handleApply = useAsyncCallback(
    (obj = EMPTY_OBJECT) => {
      if (!onApplyTableConfig) {
        return Promise.resolve()
      }

      return onApplyTableConfig({
        columns,
        view,
        ...obj,
      })
    },
    [columns, onApplyTableConfig, view]
  )

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  return {
    ...asyncTable,
    ...selection,
    entity,
    handleApply,
    handleChangeShouldReloadFilters,
    handleClickRow,
    handleConfigOpen,
    handleMouseDownRow,
    handleReloadTableAfterMassAction,
    isConfigOpen,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
    shouldReloadFilters,
  }
}
